import Vue from 'vue'
import { API_URL } from '@/config'
import router from '@/router'
import { prepareValues } from '@/lib/values'

export const presentation_persons = {

  namespaced: true,

  state: {
    roles: [],
    assigned: null,
    filters: [
      { column: 'last_name', label: 'last_name', options: { type: false }, value: '', status: true },
    ],
    options: {
      last_name: { label: 'last_name', element: 'text' },
    },
    persons: [],
    role_id: null,
    values: {},
    entities: [],
  },

  getters: {
    roles: state => state.roles,
    assigned: state => state.assigned,
    filters: state => state.filters,
    options: state => state.options,
    persons: state => state.persons,
    role_id: state => state.role_id,
    values: state => state.values,
    entities: state => state.entities,
  },

  mutations: {
    setRoles(state, roles) { 
      state.roles = roles;
      state.role_id = roles[0].value;
    },
    setAssigned(state, assigned) { state.assigned = assigned },
    setOptions(state, options) { state.options = options },
    setPersons(state, persons) { 
      for (let i in persons) persons[i].role_id = state.roles[0].value;
      state.persons = persons;
    },
    setRole(state) { 
      state.role_id = state.roles[0].value; 
    },
    setValues(state) { 
      for (let i in state.entities) {
        if (state.entities[i].column === undefined) continue;
        state.values[state.entities[i].column] = '';
      }
    },
    setEntities(state, entities) { state.entities = entities },
    clearFilters(state) {
      state.filters = [
        { column: 'last_name', label: 'last_name', options: { type: false }, value: '', status: true },
      ];
      state.options = {
        last_name: { label: 'last_name', element: 'text' },
      };
    },
  },

  actions: {

    getRoles: () => Vue.axios.get(API_URL + '/options', { params: {
      group: ['presentation_person_role'],
    }}),

    getAssigned: () => Vue.axios.get(API_URL + '/presentations/' + router.currentRoute.params.id + '/persons'),

    getOptions: () => Vue.axios.get(API_URL + '/entities', { params: {
      models: ['App\\Models\\PresentationPersonCreate'],
      filter_display: true,
    }}),

    getEntities: () => Vue.axios.get(API_URL + '/entities', { params: {
      models: ['App\\Models\\PresentationPersonCreate'],
      form_display: true,
    }}),

    async getPresentationPersons({ rootState, commit, dispatch }) {
      commit('clearResponse', {}, { root: true });
      commit('setAssigned', null);
      const [roles, assigned, options, entities] = await Promise.all([
        dispatch('getRoles'),
        dispatch('getAssigned'),
        dispatch('getOptions'),
        dispatch('getEntities'),
        commit('setValues'),
      ]);
      commit('setProgress', false, { root: true });
      commit('setRoles', roles.data.presentation_person_role.map(role => { return { value: role.value, label: role.name } }));
      commit('setAssigned', assigned.data);
      options.data['App\\Models\\PresentationPersonCreate'].filters.country_id.options = options.data['App\\Models\\PresentationPersonCreate'].filters.country_id.options.sort((a, b) => rootState._[a.name].localeCompare(rootState._[b.name]));
      commit('setOptions', options.data['App\\Models\\PresentationPersonCreate'].filters);
      let countries = entities.data['App\\Models\\PresentationPersonCreate'].findIndex(entity => entity.column === 'country_id');
      entities.data['App\\Models\\PresentationPersonCreate'][countries].options = entities.data['App\\Models\\PresentationPersonCreate'][countries].options.sort((a, b) => rootState._[a.name].localeCompare(rootState._[b.name]));
      commit('setEntities', entities.data['App\\Models\\PresentationPersonCreate']);
    },

    async addPresentationPersons({ state, rootState, commit }, params) {
      if (!rootState.progress) {
        let ids = params.ids.join(',');
        const assigned = await Vue.axios.post(API_URL + '/presentations/' + router.currentRoute.params.id + '/persons', {
          presentation_id: router.currentRoute.params.id,
          ids: ids,
          role_id: params.role_id,
        });
        commit('setPersons', state.persons.filter(person => !ids.includes(person.id)));
        commit('setProgress', false, { root: true });
        commit('clearResponse', {}, { root: true });
        commit('setRole');
        commit('setValues', {});
        commit('setAssigned', assigned.data);
      }
    },

    async updatePresentationPersons({ state, rootState, commit }) {
      if (!rootState.progress) {
        const assigned = await Vue.axios.put(API_URL + '/presentations/' + router.currentRoute.params.id + '/persons', state.assigned);
        commit('setProgress', false, { root: true });
        commit('setAssigned', assigned.data);
      }
    },

    async removePresentationPerson({ rootState, getters, commit }, id) {
      if (!rootState.progress) {
        const assigned = await Vue.axios.delete(API_URL + '/presentations/' + router.currentRoute.params.id + '/persons/' + id);
        const exclude = assigned.data.reduce((carry, presentation_person) => {
          carry.push(presentation_person.person_id);
          return carry;
        }, []);
        const persons = await Vue.axios.get(API_URL + '/persons', {
          params: { filters: getters.filters.concat([ { column: 'id', not: true, value: exclude.join(','), status: true }, ]), limit: 100, }
        });
        commit('setAssigned', assigned.data);
        commit('setPersons', persons.data.items);
        commit('setProgress', false, { root: true });
      }
    },

    async getPersons({ state, rootState, getters, commit }) {
      if (!rootState.progress) {
        let assigned = state.assigned.reduce((carry, presentation_person) => { 
          carry.push(presentation_person.person_id);
          return carry;
        }, []);
        commit('setPersons', []);
        const persons = await Vue.axios.get(API_URL + '/persons', {
          params: {
            filters: getters.filters.concat([
              { column: 'id', not: true, value: assigned.join(','), status: true },
            ]),
            limit: 100,
          }
        })
        .catch(() => {
          commit('setProgress', false, { root: true });
        });
        commit('setProgress', false, { root: true });
        commit('setPersons', persons.data.items);
      }
    },

    async validatePerson({ state, rootState, commit, dispatch }) {
      if (!rootState.progress) {
        try {
          await Vue.axios.post(API_URL + '/validate?model=App\\Models\\Person', state.values, {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });
        } catch (error) {
          commit('setProgress', false, { root: true });
          commit('setResponse', { status: true, type: 'error', message: 'form_error', errors: error.response.data.errors }, { root: true });
          return;
        }
        dispatch('existingPerson');
      }
    },

    async existingPerson({ state, commit, dispatch }) {
      const persons = await Vue.axios.post(API_URL + '/persons/exist', state.values, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      if (persons && persons.data && persons.data.length) {
        commit('setProgress', false, { root: true });
        commit('setResponse', { status: true, type: 'warning', message: persons.data, errors: [] }, { root: true });
        return;
      }
      const values = prepareValues(state.values, 'content_');
      dispatch('createPerson', values);
    },

    createPresentationPersonAnyway({ state, dispatch }) {
      const values = prepareValues(state.values, 'content_');
      dispatch('createPerson', values);
    },

    async createPerson({ state, commit, dispatch }, values) {
      const person = await Vue.axios.post(API_URL + '/persons', values);
      commit('setProgress', false, { root: true });
      dispatch('addPresentationPersons', {
        ids: [person.data.id],
        role_id: state.role_id,
      });
    },

  }
}
